<script setup lang="ts">
/**
 * A game window: the riveted steel frame (kit.css `.k-panel`) under a navy
 * enamel title plate (`.k-header`) with the title in game lettering, a painted
 * icon, and a red round close button. Every station's screen opens in one.
 *
 * The dimming overlay is part of the window: the root element is the scrim and
 * `.window` its first child, so no window can appear without one and no caller
 * draws its own backdrop. A tap on the scrim emits `close`, like the red button
 * (unless `scrimCloses` is off).
 * `level` is the stacking tier (a station window, a dialog over it, an alert
 * over everything); `docked` pins the window to the right edge (a bottom sheet
 * on a portrait phone) and lets pointers through the scrim, for the shipyard,
 * whose ship and water stay in view and touchable beside it.
 *
 * Wrap it in `SheetTransition` (with the caller's `v-if`) to animate it in and
 * out. Attributes (`data-test`, `role`, key handlers) land on the window, not
 * the scrim. The body is the default slot, the tab row is the `tabs` slot
 * (sits just under the header, over the frame), and `actions` is an optional
 * footer row. The caller owns the open/close state.
 */
import { useI18n } from '@/i18n';
import GameIcon from './GameIcon.vue';
import type { GameIconName } from './icons';

defineOptions({ inheritAttrs: false });

withDefaults(
  defineProps<{
    title: string;
    icon?: GameIconName;
    wide?: boolean;
    teak?: boolean;
    /** Any CSS width, capped to the viewport; overrides the default and `wide`. */
    width?: string;
    level?: 'window' | 'dialog' | 'alert';
    docked?: boolean;
    /** False where a stray tap must not leave (the battle results, with rewards still to read). */
    scrimCloses?: boolean;
  }>(),
  { icon: undefined, wide: false, teak: false, width: undefined, level: 'window', docked: false, scrimCloses: true },
);
const emit = defineEmits<{ close: [] }>();
const { t } = useI18n();
</script>

<template>
  <div class="scrim" :class="[`level-${level}`, { docked }]" @click.self="scrimCloses && emit('close')">
    <section class="window" :class="{ wide }" :style="width ? { '--window-w': width } : undefined" role="dialog" :aria-label="title" v-bind="$attrs">
      <header class="k-header head">
        <GameIcon v-if="icon" class="head-icon" :name="icon" :size="56" />
        <h2 class="k-title head-title">{{ title }}</h2>
        <button type="button" class="close" :aria-label="t('common.close')" data-test="window-close" @click="emit('close')">
          <GameIcon name="close" :size="52" />
        </button>
      </header>
      <div v-if="$slots.tabs" class="tabs"><slot name="tabs" /></div>
      <div class="k-panel body" :class="{ teak }">
        <div class="scroll"><slot /></div>
      </div>
      <footer v-if="$slots.actions" class="actions"><slot name="actions" /></footer>
    </section>
  </div>
</template>

<style scoped>
/* Stacking tiers: a station window over the harbour, a dialog over a window, an
   alert over everything (below `RotatePrompt`'s 101, which asks about the device). */
.scrim {
  position: fixed;
  inset: 0;
  display: grid;
  /* One row that can shrink below its content, so a tall window scrolls inside instead of overflowing. */
  grid-template-rows: minmax(0, 1fr);
  place-items: center;
  padding: calc(12px + env(safe-area-inset-top, 0px)) calc(12px + env(safe-area-inset-right, 0px)) calc(12px + env(safe-area-inset-bottom, 0px)) calc(12px + env(safe-area-inset-left, 0px));
  background: radial-gradient(ellipse at 50% 40%, rgba(6, 12, 20, 0.35), rgba(6, 12, 20, 0.75));
  /* A caller may sit inside a `pointer-events: none` layer (the shipyard); the scrim always blocks. */
  pointer-events: auto;
}

.level-window {
  z-index: 20;
}

.level-dialog {
  z-index: 40;
}

.level-alert {
  z-index: 100;
  backdrop-filter: blur(3px);
}

.window {
  --window-w: 960px;
  position: relative;
  display: flex;
  flex-direction: column;
  width: min(var(--window-w), 100%);
  max-height: 100%;
  margin: 0 auto;
  filter: drop-shadow(0 18px 30px rgba(0, 0, 0, 0.55));
}

.window.wide {
  --window-w: 1240px;
}

/* Docked: right half of the screen, the ship stays in view on the left. The scrim only
   darkens the window's side and lets the pointer through to the water. */
.scrim.docked {
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  padding-left: 12px;
  background: linear-gradient(270deg, rgba(6, 12, 20, 0.72), rgba(6, 12, 20, 0.2) 70%, transparent);
  pointer-events: none;
}

.docked .window {
  width: min(54vw, 46rem);
  height: 100%;
  max-height: none;
  margin: 0;
  pointer-events: auto;
}

/* A portrait phone has no side to give: the window is a bottom sheet under the ship. */
@media (max-width: 600px) {
  .scrim.docked {
    align-items: flex-end;
    padding: 0 calc(8px + env(safe-area-inset-right, 0px)) calc(12px + env(safe-area-inset-bottom, 0px)) calc(8px + env(safe-area-inset-left, 0px));
    background: linear-gradient(0deg, rgba(6, 12, 20, 0.72), transparent 60%);
  }

  .docked .window {
    width: 100%;
    height: 74dvh;
  }
}

.head {
  position: relative;
  z-index: 2;
  margin: 0 8px -14px;
}

.head-icon {
  margin-left: -6px;
}

.head-title {
  font-size: clamp(1.4rem, 1rem + 1.4vw, 2.1rem);
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.close {
  position: absolute;
  right: -10px;
  top: 50%;
  transform: translateY(-50%);
  display: grid;
  place-items: center;
  width: 56px;
  height: 56px;
  padding: 0;
  border: 0;
  background: none;
  cursor: pointer;
  transition: transform var(--dur) var(--ease);
}

@media (hover: hover) {
  .close:hover {
    transform: translateY(-50%) scale(1.08) rotate(6deg);
  }
}

.close:active {
  transform: translateY(-50%) scale(0.94);
}

.tabs {
  position: relative;
  z-index: 1;
  display: flex;
  gap: 6px;
  padding: 18px 28px 0;
  margin-bottom: -10px;
  overflow-x: auto;
}

.body {
  position: relative;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  padding-top: clamp(26px, 3vw, 36px);
}

/* The scroller sits inside the frame's padding: content is clipped at the inner rim
   instead of sliding over the painted brass, and the scrollbar runs inside the board. */
.scroll {
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  scrollbar-color: rgba(214, 170, 92, 0.75) rgba(0, 0, 0, 0.25);
}

.actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 10px 6px 0;
}

/* 320 px: the longest titles ("Leaderboards") fit between the icon and the close button. */
@media (max-width: 360px) {
  .head-title {
    font-size: 1.15rem;
  }
}

@media (max-height: 520px) {
  .scrim {
    padding-top: calc(6px + env(safe-area-inset-top, 0px));
    padding-bottom: calc(6px + env(safe-area-inset-bottom, 0px));
  }

  .head :deep(.game-icon) {
    width: 40px;
    height: 40px;
  }

  .close {
    width: 44px;
    height: 44px;
  }
}
</style>
